import { useMemo } from 'react';

export function AmmDepthChart({
  currentMcUsd,
  height = 80,
}: {
  currentMcUsd: number | undefined;
  height?: number;
}) {
  // SVG paths for the depth
  const { bidsPath, asksPath } = useMemo(() => {
    if (!currentMcUsd || currentMcUsd <= 0) {
      return { bidsPath: "", asksPath: "" };
    }

    const width = 300; // SVG coordinate width
    const svgHeight = 100;

    const k = 1000; // arbitrary constant for shape scaling
    const p0 = 1; // Normalized current price

    // Generate points
    const steps = 20;
    
    // Asks (Red) - Selling tokens down to 50% price
    let asksPoints = `M ${width/2},${svgHeight} `;
    for (let i = 0; i <= steps; i++) {
      const pct = i / steps; // 0 to 1
      const p = p0 * (1 - 0.5 * pct); // Price drops to 50%
      // Depth (amount of tokens needed to push price down)
      // For AMM: x = sqrt(k/p). dx = x - x0 = sqrt(k)(1/sqrt(p) - 1/sqrt(p0))
      const depth = Math.sqrt(k) * (1 / Math.sqrt(p) - 1 / Math.sqrt(p0));
      const xCoord = (width / 2) - (width / 2) * pct;
      
      // Normalize depth to svg height
      const maxDepth = Math.sqrt(k) * (1 / Math.sqrt(p0 * 0.5) - 1 / Math.sqrt(p0));
      const yCoord = svgHeight - (depth / maxDepth) * svgHeight;
      asksPoints += `L ${xCoord},${yCoord} `;
    }
    asksPoints += `L 0,${svgHeight} Z`;

    // Bids (Green) - Buying tokens up to 200% price
    let bidsPoints = `M ${width/2},${svgHeight} `;
    for (let i = 0; i <= steps; i++) {
      const pct = i / steps; // 0 to 1
      const p = p0 * (1 + 1.0 * pct); // Price rises to 200%
      // Depth (amount of SOL needed to push price up)
      // dy = y - y0 = sqrt(k)(sqrt(p) - sqrt(p0))
      const depth = Math.sqrt(k) * (Math.sqrt(p) - Math.sqrt(p0));
      const xCoord = (width / 2) + (width / 2) * pct;
      
      // Normalize depth to svg height
      const maxDepth = Math.sqrt(k) * (Math.sqrt(p0 * 2) - Math.sqrt(p0));
      const yCoord = svgHeight - (depth / maxDepth) * svgHeight;
      bidsPoints += `L ${xCoord},${yCoord} `;
    }
    bidsPoints += `L ${width},${svgHeight} Z`;

    return { bidsPath: bidsPoints, asksPath: asksPoints };
  }, [currentMcUsd]);

  if (!currentMcUsd) return null;

  return (
    <div className="relative w-full overflow-hidden border-b border-cyan-400/10 bg-black/20" style={{ height }}>
      <svg
        viewBox="0 0 300 100"
        preserveAspectRatio="none"
        className="h-full w-full opacity-60"
      >
        <defs>
          <linearGradient id="depthAskGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.0" />
          </linearGradient>
          <linearGradient id="depthBidGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
          </linearGradient>
        </defs>
        
        {/* Asks (Red / Left) */}
        <path
          d={asksPath}
          fill="url(#depthAskGrad)"
          stroke="#ef4444"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />

        {/* Bids (Green / Right) */}
        <path
          d={bidsPath}
          fill="url(#depthBidGrad)"
          stroke="#10b981"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
        
        {/* Center Line */}
        <line
          x1="150"
          y1="0"
          x2="150"
          y2="100"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="1"
          strokeDasharray="2,2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="absolute top-1 left-2 text-[10px] text-red-400/70 font-semibold tracking-wider uppercase">Sells</div>
      <div className="absolute top-1 right-2 text-[10px] text-emerald-400/70 font-semibold tracking-wider uppercase">Buys</div>
      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[10px] text-white/50 bg-black/40 px-1.5 py-0.5 rounded">
        AMM Depth
      </div>
    </div>
  );
}
